/* ============================================================================
   Mock adapter — matches a request to a route module and applies the selected
   data scenario (normal / empty / error / slow).
   The UI never sees this file: it imports the services, which call request().
   ========================================================================== */

import { ApiError, type RequestOptions } from '../http'
import { env } from '@core/utils/env'
import { getMockScenario } from './scenario'
import { accountRoutes } from './routes/account'
import { aiRoutes } from './routes/ai'
import { campaignRoutes } from './routes/campaigns'
import { careRoutes } from './routes/care'
import { communityRoutes } from './routes/community'
import { dashboardRoutes } from './routes/dashboard'
import { learnRoutes } from './routes/learn'
import { marketplaceRoutes } from './routes/marketplace'
import { plantRoutes } from './routes/plants'
import { rewardRoutes } from './routes/rewards'
import type { HandlerContext, MockRoute } from './types'

const ALL_ROUTES: MockRoute[] = [
  ...accountRoutes,
  ...dashboardRoutes,
  ...plantRoutes,
  ...careRoutes,
  ...aiRoutes,
  ...rewardRoutes,
  ...marketplaceRoutes,
  ...communityRoutes,
  ...learnRoutes,
  ...campaignRoutes,
]

interface CompiledRoute {
  route: MockRoute
  regex: RegExp
  keys: string[]
}

const COMPILED: CompiledRoute[] = ALL_ROUTES.map((route) => {
  const keys: string[] = []
  const pattern = route.pattern
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment
      keys.push(segment.slice(1))
      return '([^/]+)'
    })
    .join('/')
  return { route, regex: new RegExp(`^${pattern}$`), keys }
})

function findRoute(
  method: string,
  pathname: string,
): { route: MockRoute; params: Record<string, string> } | undefined {
  for (const compiled of COMPILED) {
    if (compiled.route.method !== method) continue
    const match = compiled.regex.exec(pathname)
    if (!match) continue
    const params: Record<string, string> = {}
    compiled.keys.forEach((key, index) => {
      params[key] = decodeURIComponent(match[index + 1])
    })
    return { route: compiled.route, params }
  }
  return undefined
}

function latencyFor(scenario: ReturnType<typeof getMockScenario>): number {
  const base = env.mockLatencyMs * (scenario === 'slow' ? 3 : 1)
  const jitter = base * 0.2 * (Math.random() - 0.5)
  return Math.max(0, Math.round(base + jitter))
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new DOMException('Aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

export async function mockRequest<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const method = options.method ?? 'GET'
  const scenario = getMockScenario()
  const [pathname] = path.split('?')

  await wait(latencyFor(scenario), options.signal)

  const found = findRoute(method, pathname)
  if (!found) {
    throw new ApiError({
      status: 500,
      code: 'mock_route_missing',
      message: `The demo adapter has no handler for ${method} ${pathname}. Add one in src/services/mock/routes.`,
    })
  }

  if (method === 'GET' && scenario === 'error') {
    throw new ApiError({
      status: 503,
      code: 'mock_service_unavailable',
      message:
        'The service is temporarily unavailable. This is the simulated error state — switch the data scenario back to Normal in Settings.',
    })
  }

  if (method === 'GET' && scenario === 'empty' && found.route.emptyState !== undefined) {
    return found.route.emptyState as TResponse
  }

  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined || value === null || value === '') continue
    query.set(key, String(value))
  }

  const context: HandlerContext = { params: found.params, query, body: options.body }
  return (await found.route.handler(context)) as TResponse
}
